/// Geometry/vector support

// NOTE: MCC angles run clockwise from top (in degrees), this converts to trig standard
export const ang2rad = (ang) => deg2rad(90 - ang);
export const rad2ang = (rad) => 90 - rad2deg(rad);

export function pointAlongRay(angle, distance) {
  let ray = vec_fromAngle(angle)
    let loc = vec_scale(ray, distance);
  return loc;
}

export function closestPointOnRay(angle, pt) {
  // HT: https://gamedev.stackexchange.com/a/131401 (taking P1 as 0,0)
  // c.f. https://en.wikipedia.org/wiki/Scalar_projection
  let ray = vec_fromAngle(angle),
    len = vec_dotProduct(ray, pt),
    loc = vec_scale(ray, Math.max(0, len));
  return loc;
}

export function rayCircleIntersection(angle, pt, r, overlapsCenter) {
  // If the ray leaving origin at `angle` intersects a circle
  // centered at `pt` with radius of `r` this will return the
  // farthest of those (usually two) intersections.

  // c.f. "Intersection of a Line and a Sphere (or circle)" section of http://paulbourke.net/geometry/circlesphere/

  // NOTE: compare with `closestPointOnRay` —but this returns early instead of clamping
  let ray = vec_fromAngle(angle),
    len = vec_dotProduct(ray, pt); //dist between 2 intersections?

  //if other circles center is in another quadrant but the circle overlaps more than one quadrant
  if (len < 0 && !overlapsCenter) return {} // ray points away from circle

  let loc = vec_scale(ray, len),
    d = vec_magnitude(vec_difference(loc, pt));
  if (r < d) return {} // circle too small to intersect

  // console.log(loc, r, d)
  // at this juncture we know that the circle does intersect the ray;
  // and `loc` represents the midpoint between those two intersections
  // (or even the single point of tangent intersection if `r === d`).
  if (r === d) return {loc: loc, d};

  /*
    Consider a triangle PLX having these vertices:

    * P is `pt` (the center of the circle)
    * L is `loc` (the `closestPointOnRay` from P)
    * X is an intersection of the circle with the ray

    Our goal is to to find X.

    PLX is a _right_ triangle: the angle at its vertex L is 90º,
    since if segment PL weren't perpendicular to ray segment LX
    then L would *not* be the closest point to P along said ray.

    We've already calculated the length of segment PL as `d`.
    And we know that the length of segment PX must be `r`.
    So by Pythagorean equation, we have also the length of LX.

    We know that vertex X is that distance |LX| from L along the ray,
    i.e. if we add it to the original `len` used to find `loc` we have X!

    (Note there are actually two X, corresponding to both the positive root
    *and* the negative root of the Pythagorean stuff! Both yield ray/circle
    intersections; we don't need the intersection closer to the ray's origin.
   */
  let xtraLen = Math.sqrt(Math.pow(r, 2) - Math.pow(d, 2)),
    xLoc = vec_scale(ray, len + xtraLen);
  return {loc: xLoc, d};
}

export function circleIntersections(p0, r0, p1, r1) {
  // HT: "Intersection of two circles" section of http://paulbourke.net/geometry/circlesphere/
  let dRay = vec_difference(p1, p0), // n.b. this is `P1 - P0` in Paul Bourke's article
    d = vec_magnitude(dRay);
  if (
    // too far apart
    d > r0 + r1 ||
    // one inside other
    d < Math.abs(r0 - r1) ||
    // identical circles [infinite intersections]
    (d === 0 && r0 === r1)
  )
    return [];

  let a = (Math.pow(r0, 2) - Math.pow(r1, 2) + Math.pow(d, 2)) / (2 * d),
    h = Math.sqrt(Math.pow(r0, 2) - Math.pow(a, 2)),
    midPoint = vec_add(p0, vec_scale(dRay, a / d)); // aka `P2` in article
  if (h === 0) {
    return [midPoint];
  } else {
    // `x3 = x2 ± h (y1 - y0) / d`
    // `y3 = y2 ± h (x1 - x0) / d`
    // CAUTION: article formulae are NOT equivalent ot `P3 = P2 ± h (P1 - P0) / d`!
    //          but rather involve a vector perpendicular to `dRay` which we call `hRay`.
    let hRay = vec_perpendicular(dRay);
    return [
      vec_add(midPoint, vec_scale(hRay, +h / d)),
      vec_add(midPoint, vec_scale(hRay, -h / d)),
    ];
  }
}

export const deg2rad = (deg) => (deg * Math.PI) / 180;
export const rad2deg = (rad) => (rad * 180) / Math.PI;
export const rad2rad = (rad) => {
  // normalizes values into [0, 2*PI) range
  const FULL_CIRCLE = Math.PI * 2;
  rad %= FULL_CIRCLE;
  if (rad < 0) {
    rad += FULL_CIRCLE;
  }
  return rad;
};
export const vec_fromAngle = (rad) => [Math.cos(rad), Math.sin(rad)];
export const vec_magnitude = (v) => Math.hypot(v[0], v[1]);
export const vec_toAngle = (v) => Math.atan2(v[1], v[0]);
export const vec_scale = (v, s) => [v[0] * s, v[1] * s];
export const vec_add = (v1, v2) => [v1[0] + v2[0], v1[1] + v2[1]];
export const vec_difference = (v2, v1) => [v2[0] - v1[0], v2[1] - v1[1]];
export const vec_dotProduct = (v1, v2) => v1[0] * v2[0] + v1[1] * v2[1];
export const vec_hadamard = (v1, v2) => [v1[0] * v2[0], v1[1] * v2[1]];
export const vec_perpendicular = (v) => [-v[1], v[0]];
export const vec_normalize = (v) => vec_scale(v, 1 / vec_magnitude(v));
